import type { CSSProperties } from 'react';

import { Link, useMatchRoute } from '@tanstack/react-router';

import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
} from '@nesso/ui/primitives/sidebar';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@nesso/ui/primitives/tooltip';

import { navigationSidebarItems } from './navigation-sidebar-items';

const sidebarWidth = { '--sidebar-width': '4rem' } as CSSProperties;

export function NavigationSidebar() {
    // The React Compiler does not handle useMatchRoute correctly — memoizing it leaves the
    // active mark on the destination that was current when the sidebar first rendered.
    'use no memo';
    const matchRoute = useMatchRoute();

    return (
        // SidebarMenuButton's own `tooltip` prop is gated on state === 'collapsed', which
        // collapsible="none" never reports, so the tooltip is wired here instead.
        // disableHoverableContent: the destinations are stacked, so moving between two of them
        // crosses the open tooltip's grace area, and Radix keeps showing the one you just left.
        <TooltipProvider delayDuration={0} disableHoverableContent>
            <SidebarProvider className="min-h-0 w-auto" style={sidebarWidth}>
                <Sidebar collapsible="none" className="items-center pt-6">
                    <SidebarContent className="w-12">
                        <SidebarGroup className="p-2">
                            <SidebarGroupContent>
                                <nav aria-label="Admin sections">
                                    <SidebarMenu>
                                        {navigationSidebarItems.map((item) => {
                                            // fuzzy because Link derives aria-current by prefix:
                                            // exact matching would unmark a nested route that Link
                                            // still announces as current.
                                            const isActive = !!matchRoute({
                                                to: item.to,
                                                fuzzy: true,
                                                includeSearch: false,
                                            });
                                            return (
                                                <SidebarMenuItem key={item.to} className="p-0">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <SidebarMenuButton
                                                                asChild
                                                                isActive={isActive}
                                                            >
                                                                <Link to={item.to}>
                                                                    <item.icon />
                                                                    {/* The accessible name; the tooltip only describes. */}
                                                                    <span className="sr-only">
                                                                        {item.label}
                                                                    </span>
                                                                </Link>
                                                            </SidebarMenuButton>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            side="right"
                                                            showArrow={false}
                                                        >
                                                            {item.label}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </SidebarMenuItem>
                                            );
                                        })}
                                    </SidebarMenu>
                                </nav>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    </SidebarContent>
                </Sidebar>
            </SidebarProvider>
        </TooltipProvider>
    );
}
